import { useEffect, useState } from "react";

/**
 * The trailing edge of a rapidly changing value: returns `value` only once it has stopped changing
 * for `delayMs`. Written for the Thank You Notes search box (GFG-234), where each keystroke now
 * costs a network round-trip instead of an in-memory filter, but deliberately generic — it knows
 * nothing about the feed.
 *
 * The effect's cleanup runs before every re-run as well as on unmount, so a burst of keystrokes
 * only ever lands the last one, and a pending timer can never fire into an unmounted component.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
